import React, { useState, useEffect, useMemo } from "react";
import { apiPost } from "../../api";
import { SvgIcon } from "../SvgIcon";
import { BtnSpinner } from "../BtnSpinner";
import { COUNTRY_PHONE_CODES } from "../../data/countryPhoneCodes";

interface Country {
  code: string;
  dialCode: string;
  name: string;
}

// Build sorted country list once using browser Intl API
const _displayNames = new Intl.DisplayNames(["en"], { type: "region" });
const COUNTRIES: Country[] = Object.entries(COUNTRY_PHONE_CODES)
  .map(([code, dialCode]) => ({
    code,
    dialCode,
    name: _displayNames.of(code) || code
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

function toE164(dialCode: string, localInput: string): string {
  // Strip everything except digits
  const digits = localInput.replace(/\D/g, "");
  // Strip leading 0 (trunk prefix used in many countries)
  const stripped = digits.startsWith("0") ? digits.slice(1) : digits;
  return `${dialCode}${stripped}`;
}

function maskPhone(e164: string): string {
  if (e164.length < 6) return e164;
  return e164.slice(0, 3) + "••••" + e164.slice(-3);
}

export function RecoveryPhoneModal(props: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (maskedPhone: string) => void;
}) {
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [countryCode, setCountryCode] = useState("SE");
  const [phoneInput, setPhoneInput] = useState("");
  const [otp, setOtp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedPhone, setSubmittedPhone] = useState("");

  useEffect(() => {
    if (!props.isOpen) {
      setStep("phone");
      setCountryCode("SE");
      setPhoneInput("");
      setOtp("");
      setSubmitting(false);
      setError(null);
      setSubmittedPhone("");
    }
  }, [props.isOpen]);

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0],
    [countryCode]
  );

  async function handlePhoneSubmit() {
    if (!phoneInput.trim()) {
      setError("Please enter a phone number.");
      return;
    }

    const e164 = toE164(selectedCountry.dialCode, phoneInput);
    if (!/^\+[1-9]\d{6,14}$/.test(e164)) {
      setError("Please enter a valid phone number.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const res = await apiPost<{ ok: boolean }>("/v1/account/recovery/phone", { phone: e164 });

    setSubmitting(false);

    if (!res.ok) {
      setError(res.error || "Failed to save phone number.");
      return;
    }

    setSubmittedPhone(e164);
    setStep("otp");
  }

  async function handleOtpSubmit() {
    if (!otp.trim()) {
      setError("Please enter the verification code.");
      return;
    }

    setSubmitting(true);
    setError(null);

    // Backend needs the phone to resolve which destination the OTP verifies
    // (Model A supports multiple phones). submittedPhone holds the E.164 value.
    const res = await apiPost<{ ok: boolean }>("/v1/account/recovery/phone/verify", { otp: otp.trim(), phone: submittedPhone });

    setSubmitting(false);

    if (!res.ok) {
      setError(res.error || "Invalid or expired code. Please try again.");
      return;
    }

    props.onSuccess(maskPhone(submittedPhone));
    props.onClose();
  }

  if (!props.isOpen) return null;

  return (
    <div className="modalBackdrop" role="dialog" aria-modal="true">
      <div className="modalCard" style={{ width: "min(460px, 92vw)" }}>
        <div className="modalHeader">
          <div className="modalTitle">
            {step === "phone" ? "Add phone number" : "Verify phone number"}
          </div>
          <button className="iconBtn" onClick={props.onClose} title="Close" disabled={submitting}>
            <SvgIcon name="x-circle" title="Close" />
          </button>
        </div>

        <div className="modalBody" style={{ padding: "16px 20px" }}>
          {step === "phone" ? (
            <>
              <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5" }}>
                Add a phone number to receive SMS alerts and use as a recovery method.
              </div>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                Country
              </label>
              <select
                className="input"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                disabled={submitting}
                style={{ width: "100%", boxSizing: "border-box", marginBottom: "12px" }}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.dialCode})
                  </option>
                ))}
              </select>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                Phone number
              </label>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <div style={{
                  padding: "8px 10px",
                  border: "1px solid var(--control-border)",
                  borderRadius: "4px",
                  fontSize: "13px",
                  color: "var(--muted)",
                  background: "var(--surface)",
                  minWidth: "56px",
                  textAlign: "center"
                }}>
                  {selectedCountry.dialCode}
                </div>
                <input
                  type="tel"
                  className="input"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handlePhoneSubmit(); }}
                  placeholder="701 234 567"
                  disabled={submitting}
                  autoFocus
                  style={{ flex: 1, boxSizing: "border-box" }}
                />
              </div>
              <div style={{ fontSize: "11px", color: "var(--muted)", marginTop: "5px" }}>
                Enter local number without country code. Leading zeros are stripped automatically.
              </div>

              {error && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "10px" }}>
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontSize: "13px", color: "var(--muted)", marginBottom: "16px", lineHeight: "1.5" }}>
                Enter the 6-digit code sent to <strong>{submittedPhone}</strong>.
              </div>

              <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px" }}>
                Verification code
              </label>
              <input
                type="text"
                inputMode="numeric"
                className="input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => { if (e.key === "Enter") handleOtpSubmit(); }}
                placeholder="123456"
                maxLength={6}
                disabled={submitting}
                autoFocus
                style={{ width: "100%", boxSizing: "border-box", letterSpacing: "0.2em", fontSize: "16px" }}
              />

              {error && (
                <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "10px" }}>
                  {error}
                </div>
              )}

              <button
                onClick={() => { setStep("phone"); setOtp(""); setError(null); }}
                disabled={submitting}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "var(--accent)",
                  padding: "0",
                  cursor: "pointer",
                  fontSize: "12px",
                  marginTop: "10px"
                }}
              >
                <SvgIcon name="arrow-left" title="Back" /> Change number
              </button>
            </>
          )}
        </div>

        <div className="modalFooter" style={{ padding: "12px 20px" }}>
          <button className="btn" onClick={props.onClose} disabled={submitting}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button
            className="btn primary"
            onClick={step === "phone" ? handlePhoneSubmit : handleOtpSubmit}
            disabled={submitting || (step === "phone" ? !phoneInput.trim() : otp.length < 6)}
          >
            <BtnSpinner loading={submitting}>
              {submitting
                ? step === "phone" ? "Sending…" : "Verifying…"
                : step === "phone" ? "Send code" : "Verify"}
            </BtnSpinner>
          </button>
        </div>
      </div>
    </div>
  );
}
